import { Inject, Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import { jwtDecode } from 'jwt-decode';
import { WINDOW } from '../app-config/factories/window.factory';

@Injectable({ providedIn: 'root' })
export class TokenService {
    readonly className = 'TokenService';

    // set from config
    private isProd = false;
    private cookieReadDelayMs = 0;
    private cookieReadMaxTries = 0;

    private cookiePromise: Promise<string> | undefined;
    private didGetCookieToken = false;
    private token: string | undefined;

    constructor(
        readonly configService: ConfigService,
        private readonly consoleLogService: ConsoleLogService,
        @Inject(WINDOW) private readonly window: Window
    ) {
        this.isProd = configService.isProd;
        this.cookieReadDelayMs = configService.auth.cookieReadDelayMs;
        this.cookieReadMaxTries = configService.auth.cookieReadMaxTries;
    }

    async getToken(): Promise<string> {
        if (!this.isProd && this.window.rdcAuthTokenOverride) {
            this.token = this.window.rdcAuthTokenOverride;
        }

        if (!this.token) {
            this.token = await this.waitForAuthTokenCookie();
        }

        if (this.isTokenExpired(this.token)) {
            this.token = '';
        }

        return this.token;
    }

    isTokenExpired(token: string): boolean {
        let expiryInSeconds: number;

        try {
            expiryInSeconds = jwtDecode(token)?.exp ?? 0;
        } catch {
            expiryInSeconds = 0;
        }

        if (!expiryInSeconds) {
            this.consoleLogService.debug('auth token has no expiration value');
            return true;
        }
        const expirationDate = new Date(expiryInSeconds * 1000);
        this.consoleLogService.debug('auth expiration:', expirationDate);

        return new Date() >= expirationDate;
    }

    /**
     * Retrieves whether a JWT is stale. Staleness is defined as a certain amount of time from the JWT issued at time.
     * @param token the JWT
     * @param offsetSeconds
     * @returns whether the JWT is stale
     */
    isTokenStale(token: string, offsetSeconds = 3600): boolean {
        let issueTimeInSeconds: number;

        try {
            issueTimeInSeconds = jwtDecode(token)?.iat ?? 0;
        } catch {
            issueTimeInSeconds = 0;
        }

        if (!issueTimeInSeconds) {
            return true;
        }
        const staleDate = new Date((issueTimeInSeconds + offsetSeconds) * 1000);
        return new Date() >= staleDate;
    }

    private getTokenFromAuthCookie(): string {
        return (
            this.window.document.cookie
                .split(';')
                .map((cookie) => {
                    const index = cookie.indexOf('=');
                    const key = cookie.slice(0, index).trim();
                    const value = cookie.slice(index + 1).trim();
                    return [key, value];
                })
                .find(([key]) => key === 'accesstoken')?.[1] || ''
        );
    }

    private async waitForAuthTokenCookie(): Promise<string> {
        if (
            this.configService.browser.firefoxVersion === 0 ||
            !this.cookieReadDelayMs ||
            !this.cookieReadMaxTries ||
            this.didGetCookieToken
        ) {
            return Promise.resolve(this.getTokenFromAuthCookie());
        } else if (!this.cookiePromise) {
            this.cookiePromise = new Promise((resolve) => {
                let attempts = 1;
                const interval = setInterval(() => {
                    const token = this.getTokenFromAuthCookie();
                    if (!token) {
                        if (attempts >= this.cookieReadMaxTries) {
                            clearInterval(interval);
                            this.didGetCookieToken = true;
                            this.consoleLogService.debug(
                                `failed to get auth token after ${String(
                                    attempts
                                )} attempts`
                            );
                            resolve('');
                        } else {
                            this.consoleLogService.debug(
                                `failed to get auth token cookie, attempt ${attempts}`
                            );
                        }
                    } else {
                        clearInterval(interval);
                        this.didGetCookieToken = true;
                        this.consoleLogService.debug(
                            'successfully read auth cookie',
                            token
                        );

                        if (attempts > 1) {
                            // Only log if cookie read successful after more than one attempt.
                            // There will always be multiple attempts on Firefox when auth cookie is absent.
                            void this.logToCloud(
                                `cookie read took ${attempts} attempts (${attempts * (this.cookieReadDelayMs - 1)}ms)`
                            );
                        }

                        resolve(token);
                    }
                    attempts++;
                }, this.cookieReadDelayMs);
            });
        }

        return this.cookiePromise;
    }

    private async logToCloud(message: string): Promise<void> {
        try {
            const logEntry = {
                data: { warnMessage: message },
                dateTime: new Date(),
                level: 'Warn',
                source: `Portal.${this.className}`,
            };

            const logData = { entries: [logEntry] };
            const url = `${this.configService.apiUrl.rideCommand}/log`;

            await fetch(url, {
                body: JSON.stringify(logData),
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'POST',
            });
        } catch (error) {
            this.consoleLogService.error('Failed to log to cloud:', error);
        }
    }
}
